import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileSignature,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  RefreshCw,
  Clock,
  User,
  Mail,
  Calendar,
  Building2,
  FileText,
  Pen,
  X,
  Shield
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Signature Canvas Component
const SignatureCanvas = ({ onSave, signerName }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!hasSignature) {
      toast.error('Please sign the document first');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg p-4 border-2 border-dashed border-slate-300">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full h-auto touch-none cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <p className="text-center text-sm text-slate-500 mt-2">
          Sign above using your mouse or finger
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={clearCanvas}
          className="px-4 py-2 text-slate-600 hover:text-slate-900 flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Clear</span>
        </button>
        <button
          onClick={handleSave}
          disabled={!hasSignature}
          className="px-6 py-3 bg-neon-cyan text-slate-900 rounded-lg font-semibold hover:bg-neon-cyan/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Pen className="w-4 h-4" />
          <span>Apply Signature</span>
        </button>
      </div>
    </div>
  );
};

// Main Component
export const ContractSigningPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [signerInfo, setSignerInfo] = useState(null);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  useEffect(() => {
    fetchSigningData();
  }, [token]);

  const fetchSigningData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contract-builder/sign/${token}`);
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to load document');
      }
      
      const data = await res.json();
      setContract(data.contract);
      setSignerInfo({
        name: data.signer_name,
        email: data.signer_email,
        sentAt: data.sent_at,
        expiresAt: data.expires_at,
        status: data.status
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureCapture = useCallback((dataUrl) => {
    setSignatureData(dataUrl);
    setShowSignatureModal(false);
    toast.success('Signature captured! Click "Complete Signing" to submit.');
  }, []);

  const handleSubmitSignature = async () => {
    if (!signatureData) {
      toast.error('Please add your signature first');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contract-builder/sign/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data_url: signatureData,
          signer_name: signerInfo.name
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit signature');
      }

      setSigned(true);
      toast.success('Document signed successfully!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-neon-cyan animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Document</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <a href="https://medexova.com" className="text-neon-cyan hover:underline">
            Return to MedexOVA
          </a>
        </div>
      </div>
    );
  }

  // Success state
  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-400" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-white mb-2">Document Signed!</h1>
          <p className="text-slate-400 mb-6">
            Thank you, {signerInfo?.name}. Your signature has been recorded and the document is now complete.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Signed document:</span>
              <span className="text-white font-medium">{contract?.template_name}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-slate-400">Timestamp:</span>
              <span className="text-neon-cyan">{new Date().toLocaleString()}</span>
            </div>
          </div>
          
          <div className="p-4 bg-neon-cyan/10 rounded-lg border border-neon-cyan/30">
            <Shield className="w-5 h-5 text-neon-cyan mx-auto mb-2" />
            <p className="text-sm text-slate-300">
              A copy of the signed document will be sent to your email address.
            </p>
          </div>
          
          <p className="text-slate-500 text-sm mt-6">
            You may close this window.
          </p>
        </motion.div>
      </div>
    );
  }

  // Main signing view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileSignature className="w-8 h-8 text-neon-cyan" />
            <div>
              <h1 className="text-xl font-bold text-white">MedexOVA</h1>
              <p className="text-xs text-slate-400">Document Signing</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center text-slate-400">
              <User className="w-4 h-4 mr-1" />
              {signerInfo?.name}
            </div>
            <div className="flex items-center text-slate-400">
              <Clock className="w-4 h-4 mr-1" />
              Expires {new Date(signerInfo?.expiresAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Document Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 mb-6"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-6 h-6 text-neon-cyan" />
                <h2 className="text-2xl font-bold text-white">{contract?.template_name}</h2>
              </div>
              <div className="flex items-center space-x-4 text-sm text-slate-400">
                <span className="flex items-center">
                  <Building2 className="w-4 h-4 mr-1" />
                  {contract?.field_values?.partner_company || 
                   contract?.field_values?.facility_name || 
                   'MedexOVA'}
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Sent {new Date(signerInfo?.sentAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
              Awaiting Your Signature
            </span>
          </div>
        </motion.div>

        {/* Contract Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Document Preview</h3>
          
          <div className="bg-white rounded-lg p-6 max-h-[500px] overflow-y-auto">
            <div className="text-slate-800">
              {/* Render contract fields */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="text-lg font-bold text-slate-900 mb-4">{contract?.template_name}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(contract?.field_values || {}).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-xs text-slate-500 uppercase">{key.replace(/_/g, ' ')}</span>
                      <p className="text-slate-800">{String(value)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  This agreement (&quot;Agreement&quot;) is entered into as of the date of signature below.
                </p>
                <p className="mb-4">
                  By signing below, you acknowledge that you have read, understand, and agree to be bound by all terms and conditions of this agreement.
                </p>
                <p>
                  This is a legally binding document. Please read carefully before signing.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Signature Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Your Signature</h3>
          
          {signatureData ? (
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img 
                    src={signatureData} 
                    alt="Your signature" 
                    className="h-16 bg-white rounded p-2"
                  />
                  <div>
                    <p className="text-white font-medium">{signerInfo?.name}</p>
                    <p className="text-sm text-slate-400">Ready to submit</p>
                  </div>
                </div>
                <button
                  onClick={() => setSignatureData(null)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowSignatureModal(true)}
              className="w-full py-4 border-2 border-dashed border-neon-cyan/50 rounded-lg text-neon-cyan hover:bg-neon-cyan/10 flex items-center justify-center space-x-2 transition-colors"
            >
              <Pen className="w-5 h-5" />
              <span>Click to Sign</span>
            </button>
          )}
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={handleSubmitSignature}
            disabled={!signatureData || submitting}
            className="w-full py-4 bg-gradient-to-r from-neon-cyan to-neon-green text-slate-900 rounded-xl font-bold text-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Complete Signing</span>
              </>
            )}
          </button>
          <p className="text-center text-sm text-slate-500 mt-4">
            By clicking &quot;Complete Signing&quot;, you agree that your electronic signature is legally binding.
          </p>
        </motion.div>
      </main>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSignatureModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">Sign Document</h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-slate-600 mb-4">
              Signing as: <strong>{signerInfo?.name}</strong>
            </p>
            
            <SignatureCanvas 
              onSave={handleSignatureCapture}
              signerName={signerInfo?.name}
            />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContractSigningPage;
